import React, { useState, useEffect, Suspense, lazy } from 'react';
import { LandingPage } from './components/LandingPage';
import { BookProject, Section, Segment, PlanTier, User, GuestSession, StoryBible } from './types';
import {
  getStoredSession,
  getStoredUser,
  logout as authLogout,
  updateUserPlan,
  refreshUserBooks,
  registerBook,
  updateStoredUser
} from './services/authService';
import {
  getGuestSession,
  createGuestSession,
  getGuestProject,
  saveGuestProject,
  clearGuestSession,
  GuestBlockReason,
  saveGuestStateToServer
} from './services/guestService';
import './src/i18n'; // Initialize i18n
import { handlePaymentCallback, verifyPayment, createSingleBookCheckout, redirectToCheckout, createCheckoutSession } from './services/stripeService';
import { initializeMobilePayments, purchaseSubscription, purchaseSingleBook as purchaseSingleBookNative, isMobilePlatform } from './services/mobilePaymentService';
import { ErrorBoundary } from './components/ErrorBoundary';

// Capacitor — only import core (lightweight), defer heavy plugins to native init
import { Capacitor } from '@capacitor/core';

// Lazy load heavy components (only loaded when needed)
const ProjectSetup = lazy(() => import('./components/ProjectSetup').then(m => ({ default: m.ProjectSetup })));
const WriterWorkspace = lazy(() => import('./components/WriterWorkspace').then(m => ({ default: m.WriterWorkspace })));
const AccountPanel = lazy(() => import('./components/AccountPanel').then(m => ({ default: m.AccountPanel })));
const AdminPanel = lazy(() => import('./components/AdminPanel'));

// Lazy load components not needed for initial landing page render
const AuthModal = lazy(() => import('./components/AuthModal').then(m => ({ default: m.AuthModal })));
const UpgradeModal = lazy(() => import('./components/UpgradeModal').then(m => ({ default: m.UpgradeModal })));
const BlogPost = lazy(() => import('./components/BlogPost').then(m => ({ default: m.BlogPost })));
const BlogListPage = lazy(() => import('./components/BlogListPage').then(m => ({ default: m.BlogListPage })));

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
      <p className="text-slate-500 font-medium">Cargando...</p>
    </div>
  </div>
);

const App: React.FC = () => {
  // Auth state
  const [user, setUser] = useState<User | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>('register');
  const [showAccountPanel, setShowAccountPanel] = useState(false);

  // Guest mode state
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [guestSession, setGuestSession] = useState<GuestSession | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState<GuestBlockReason>('second_segment');
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [pendingPlan, setPendingPlan] = useState<PlanTier | null>(() => {
    // Recover pending plan from sessionStorage (survives page refresh during auth)
    const stored = sessionStorage.getItem('pending_subscription_plan');
    return stored ? (stored as PlanTier) : null;
  });

  // Project state
  const [project, setProject] = useState<BookProject | null>(null);
  const [blogSlug, setBlogSlug] = useState<string | null>(null);
  const [navTrigger, setNavTrigger] = useState(0);

  useEffect(() => {
    console.log('App: showAuthModal changed to:', showAuthModal);
  }, [showAuthModal]);

  // Listen to popstate to force re-render on history changes
  useEffect(() => {
    const handlePopState = () => {
      setNavTrigger(prev => prev + 1);
      // Reset blog slug if we went back to root or /blog
      const path = window.location.pathname.replace(/\/+$/, '') || '/';
      if (path === '/' || path === '/blog') {
        setBlogSlug(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Native platform initialization (Capacitor) — dynamically import heavy plugins
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // Add CSS class for mobile-specific styles (safe areas, etc.)
      document.body.classList.add('capacitor-app');

      // Dynamically import Capacitor plugins only on native (saves ~10KB on web)
      Promise.all([
        import('@capacitor/status-bar'),
        import('@capacitor/splash-screen'),
        import('@capacitor/app')
      ]).then(([{ StatusBar, Style }, { SplashScreen }, { App: CapApp }]) => {
        // Configure StatusBar to match app theme
        StatusBar.setStyle({ style: Style.Dark }).catch(() => { });
        StatusBar.setBackgroundColor({ color: '#050505' }).catch(() => { });

        // Hide splash screen once the app is ready
        SplashScreen.hide().catch(() => { });

        // Handle Android hardware back button
        CapApp.addListener('backButton', ({ canGoBack }) => {
          if (!canGoBack) {
            import('@capacitor/app').then(({ App }) => App.exitApp());
          } else {
            window.history.back();
          }
        });
      }).catch(() => { });

      // Initialize RevenueCat for native purchases
      initializeMobilePayments().catch(err => console.error('RevenueCat init error:', err));
    }
  }, []);

  // Load session on mount
  useEffect(() => {
    const initAuth = async () => {
      console.log('App: initAuth started'); // DEBUG
      const session = getStoredSession();
      const storedUser = getStoredUser();

      if (session && storedUser) {
        // Validate that the stored user has critical fields
        // (earlier bugs may have stored partial user objects missing plan, name, etc.)
        if (!storedUser.plan || !storedUser.name || !storedUser.email) {
          console.warn('App: Stored user is corrupted, attempting recovery from backend...');
          try {
            const { refreshUserProfile } = await import('./services/authService');
            const result = await refreshUserProfile(storedUser.id);
            if (result.success && result.user) {
              setUser(result.user);
            } else {
              // Fallback: still set the user so they're not logged out
              setUser(storedUser);
            }
          } catch (e) {
            console.error('App: Failed to recover user profile:', e);
            setUser(storedUser);
          }
        } else {
          setUser(storedUser);
        }
      } else {
        // Check for existing guest session
        const existingGuest = getGuestSession();
        if (existingGuest) {
          setGuestSession(existingGuest);
          setIsGuestMode(true);
          // Load guest project if exists
          const guestProject = getGuestProject();
          if (guestProject) {
            setProject(guestProject);
          }
        }
      }

      // Handle payment callback
      const paymentResult = handlePaymentCallback();
      console.log('App: paymentResult', paymentResult); // DEBUG

      if (paymentResult.isPaymentCallback && paymentResult.success && paymentResult.sessionId) {
        console.log('App: Payment success, plan:', paymentResult.plan); // DEBUG

        // Check if this is a single book purchase (guest mode)
        if (paymentResult.plan === 'single_book') {
          console.log('App: Single book purchase detected, opening AuthModal'); // DEBUG
          // Guest completed single book purchase - they need to create account now
          setAuthModalMode('register');
          setShowAuthModal(true);
          console.log('App: setShowAuthModal set to true (from payment)'); // DEBUG
          // Store payment info for after registration
          sessionStorage.setItem('pending_single_book_payment', JSON.stringify({
            sessionId: paymentResult.sessionId,
            guestId: new URLSearchParams(window.location.search).get('guestId')
          }));
        } else if (storedUser) {
          // Regular subscription payment for logged-in user
          const verifyResult = await verifyPayment(storedUser.id, paymentResult.sessionId);

          if (verifyResult.success) {
            const updateResult = await updateUserPlan(storedUser.id, paymentResult.plan || 'starter');
            if (updateResult.success && updateResult.user) {
              setUser(updateResult.user);
            }
          }
        }
      }

      setIsAuthLoading(false);
    };

    initAuth();
  }, []);

  const handleUpdateSection = (chapterIndex: number, sectionIndex: number, updates: Partial<Section>) => {
    setProject(prev => {
      if (!prev) return null;
      const newOutline = [...prev.outline];
      const chapter = { ...newOutline[chapterIndex] };
      const sections = [...chapter.sections];
      const section = { ...sections[sectionIndex], ...updates };

      sections[sectionIndex] = section;
      chapter.sections = sections;
      newOutline[chapterIndex] = chapter;

      const updated = { ...prev, outline: newOutline };

      // Auto-save for guest mode
      if (isGuestMode) {
        saveGuestProject(updated);
        if (guestSession) {
          saveGuestStateToServer(guestSession.guestId, updated);
        }
      }

      return updated;
    });
  };

  // Handle segment updates for novels (4-level structure)
  const handleUpdateSegment = (chapterIndex: number, sceneIndex: number, segmentIndex: number, updates: Partial<Segment>) => {
    setProject(prev => {
      if (!prev || !prev.novelOutline) return prev;

      const newNovelOutline = [...prev.novelOutline];
      const chapter = { ...newNovelOutline[chapterIndex] };
      const scenes = [...chapter.scenes];
      const scene = { ...scenes[sceneIndex] };
      const segments = [...scene.segments];
      const segment = { ...segments[segmentIndex], ...updates };

      segments[segmentIndex] = segment;
      scene.segments = segments;
      scenes[sceneIndex] = scene;
      chapter.scenes = scenes;
      newNovelOutline[chapterIndex] = chapter;

      const updated = { ...prev, novelOutline: newNovelOutline, lastUpdated: Date.now() };

      // Auto-save for guest mode
      if (isGuestMode) {
        saveGuestProject(updated);
        if (guestSession) {
          saveGuestStateToServer(guestSession.guestId, updated);
        }
      }

      return updated;
    });
  };

  // Handle Bible updates (immutable, triggers React re-render + auto-save)
  const handleUpdateBible = (newBible: StoryBible) => {
    setProject(prev => {
      if (!prev) return prev;
      return { ...prev, bible: newBible };
    });
  };

  const handleAuthSuccess = async (authenticatedUser: User) => {
    setUser(authenticatedUser);
    setShowAuthModal(false);

    // Check for pending single book payment
    const pendingPayment = sessionStorage.getItem('pending_single_book_payment');
    if (pendingPayment) {
      try {
        const { sessionId, guestId } = JSON.parse(pendingPayment);
        console.log('App: Processing pending payment', sessionId);

        if (authenticatedUser && sessionId) {
          // Import the specific single book verification function
          const { verifySingleBookPayment } = await import('./services/stripeService');

          // Verify payment and update user plan to 'single_book' in backend
          const result = await verifySingleBookPayment(authenticatedUser.id, sessionId);

          if (result.success) {
            console.log('App: Single book payment verified', result);
            const updatedUser = {
              ...authenticatedUser,
              plan: 'single_book' as PlanTier,
              booksRemaining: 1
            };
            setUser(updatedUser);
            updateStoredUser(updatedUser);
            sessionStorage.removeItem('pending_single_book_payment');
          } else {
            console.error('App: Payment verification failed', result.error);
            alert('Error verificando el pago: ' + result.error);
          }
        }
      } catch (e) {
        console.error('App: Error parsing pending payment', e);
      }
    }

    // If coming from guest mode, migrate the project
    if (isGuestMode && project && guestSession) {
      setIsGuestMode(false);
      clearGuestSession();
      setGuestSession(null);
    }

    // Check for pending subscription plan → redirect to Stripe checkout
    const storedPlan = pendingPlan || sessionStorage.getItem('pending_subscription_plan') as PlanTier | null;
    if (storedPlan && storedPlan !== 'free') {
      console.log('App: Pending plan detected after auth, redirecting to Stripe:', storedPlan);
      setPendingPlan(null);
      sessionStorage.removeItem('pending_subscription_plan');

      try {
        const checkoutResult = await createCheckoutSession(
          authenticatedUser.id,
          authenticatedUser.email,
          storedPlan,
          authenticatedUser.name
        );

        if (checkoutResult.success && checkoutResult.url) {
          redirectToCheckout(checkoutResult.url);
          return; // User is being redirected, stop further processing
        } else {
          console.error('App: Failed to create checkout session:', checkoutResult.error);
          alert('Error al iniciar el proceso de pago: ' + (checkoutResult.error || 'Inténtalo de nuevo'));
        }
      } catch (e) {
        console.error('App: Error creating checkout after auth:', e);
        alert('Error de conexión al procesar el pago. Inténtalo desde tu panel de cuenta.');
      }
    }
  };

  const handleLogout = () => {
    authLogout();
    setUser(null);
    setProject(null);
    setShowAccountPanel(false);
    setIsGuestMode(false);
    setGuestSession(null);
  };

  const handleStartTrial = (mode: 'login' | 'register' = 'register') => {
    setAuthModalMode(mode);
    setShowAuthModal(true);
  };

  // NEW: Handle starting guest mode
  const handleStartGuest = () => {
    const session = createGuestSession();
    setGuestSession(session);
    setIsGuestMode(true);
  };

  const handleProjectCreated = (newProject: BookProject) => {
    setProject(newProject);

    // Save for guest mode
    if (isGuestMode && guestSession) {
      saveGuestProject(newProject);
      saveGuestStateToServer(guestSession.guestId, newProject);
    }

    if (user) {
      let updatedUser = { ...user };

      // Deduct credit for paid plans
      if (user.plan !== 'free' && user.plan !== 'single_book' && user.booksRemaining > 0) {
        // Register book on backend — this properly decrements booksRemaining in the spreadsheet
        registerBook(user.id, { id: newProject.id, title: newProject.title }).then(result => {
          if (result.success && result.remaining !== undefined) {
            setUser(prev => prev ? { ...prev, booksRemaining: result.remaining! } : prev);
          } else {
            // Fallback: optimistic local decrement
            setUser(prev => prev ? { ...prev, booksRemaining: Math.max(0, prev.booksRemaining - 1) } : prev);
          }
        }).catch(() => {
          // Fallback on error
          setUser(prev => prev ? { ...prev, booksRemaining: Math.max(0, prev.booksRemaining - 1) } : prev);
        });
      }
      // Mark free usage
      else if (user.plan === 'free') {
        updatedUser = { ...updatedUser, freeGenerationUsed: true };
        if (typeof window !== 'undefined') {
          import('./services/authService').then(service => {
            service.markFreeGenerationUsed(user);
          });
        }
      }
      setUser(updatedUser);
    }
  };

  const handleProjectLoaded = (loadedProject: BookProject) => {
    setProject(loadedProject);
  };

  const handleBackToSetup = () => {
    setProject(null);
  };

  const handlePlanUpdated = (updatedUser: User) => {
    setUser(updatedUser);
  };

  // Guest upgrade handlers
  const handleUpgradeRequired = (reason: GuestBlockReason) => {
    setUpgradeReason(reason);
    setShowUpgradeModal(true);
  };

  const handleSubscribe = async (plan: PlanTier) => {
    // On mobile: trigger native purchase directly via RevenueCat
    if (isMobilePlatform()) {
      setShowUpgradeModal(false);
      setIsCheckoutLoading(true);
      const result = await purchaseSubscription(plan);
      setIsCheckoutLoading(false);

      if (result.success) {
        alert('¡Compra exitosa! Tu plan ha sido activado.');
        // TODO: sync user plan from backend webhook or RC customerInfo
      } else if (result.error && result.error !== 'Compra cancelada') {
        alert('Error: ' + result.error);
      }
      return;
    }

    // On web: save the selected plan, then register → after auth, redirect to Stripe
    setPendingPlan(plan);
    sessionStorage.setItem('pending_subscription_plan', plan);
    setShowUpgradeModal(false);
    setAuthModalMode('register');
    setShowAuthModal(true);
  };

  const handleBuySingleBook = async () => {
    if (!guestSession || !project) return;
    setIsCheckoutLoading(true);

    // On mobile: trigger native consumable purchase via RevenueCat
    if (isMobilePlatform()) {
      const result = await purchaseSingleBookNative();
      setIsCheckoutLoading(false);

      if (result.success) {
        alert('¡Compra exitosa! Ya puedes escribir tu libro completo.');
        // TODO: sync purchase status from backend webhook
      } else if (result.error && result.error !== 'Compra cancelada') {
        alert('Error: ' + result.error);
      }
      return;
    }

    // On web: use Stripe checkout
    const email = "";
    const result = await createSingleBookCheckout(
      email,
      guestSession.guestId,
      project.id
    );

    if (result.success && result.url) {
      redirectToCheckout(result.url);
    } else {
      alert('Error al procesar el pago: ' + (result.error || 'Inténtalo de nuevo'));
      setIsCheckoutLoading(false);
    }
  };

  // *** ADMIN PANEL ROUTE *** (Hidden URL: /admin)
  if (window.location.pathname === '/admin' || window.location.hash === '#/admin') {
    return (
      <ErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          <AdminPanel />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // *** BLOG ROUTES ***
  // Normalize pathname: remove trailing slash for consistent matching
  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';

  // *** BLOG INDEX ROUTE ***
  const isBlogIndex = normalizedPath === '/blog';
  if (isBlogIndex && !blogSlug) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <BlogListPage
          onOpenPost={(slug) => {
            setBlogSlug(slug);
            window.history.pushState({}, '', `/blog/${slug}`);
          }}
          onBack={() => {
            window.history.pushState({}, '', '/');
            setBlogSlug(null);
            const navEvent = new PopStateEvent('popstate');
            window.dispatchEvent(navEvent);
          }}
        />
      </Suspense>
    );
  }

  // *** BLOG POST ROUTE ***
  const blogMatch = normalizedPath.match(/^\/blog\/(.+)$/);
  if (blogMatch || blogSlug) {
    const slug = blogSlug || blogMatch![1];
    return (
      <Suspense fallback={<LoadingFallback />}>
        <BlogPost
          slug={slug}
          onBack={() => {
            setBlogSlug(null);
            window.history.pushState({}, '', '/blog/');
          }}
        />
      </Suspense>
    );
  }

  // Show loading state
  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500 font-medium">Cargando...</p>
        </div>
      </div>
    );
  }

  // Show landing page if not authenticated AND not in guest mode
  if (!user && !isGuestMode) {
    return (
      <>
        <LandingPage
          onStartTrial={() => handleStartTrial('register')}
          onStartGuest={handleStartGuest}
          onLogin={() => handleStartTrial('login')}
          onOpenBlog={(slug) => {
            setBlogSlug(slug);
            window.history.pushState({}, '', `/blog/${slug}/`);
          }}
        />
        {showAuthModal && (
          <Suspense fallback={null}>
            <AuthModal
              isOpen={showAuthModal}
              onClose={() => setShowAuthModal(false)}
              onAuthSuccess={handleAuthSuccess}
              initialMode={authModalMode}
            />
          </Suspense>
        )}
      </>
    );
  }

  // Guest mode flow (no user but in guest mode)
  if (isGuestMode && !user) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          <div className="min-h-screen bg-transparent">
            {/* Upgrade Modal for guests */}
            <UpgradeModal
              isOpen={showUpgradeModal}
              onClose={() => setShowUpgradeModal(false)}
              reason={upgradeReason}
              guestProject={project}
              onSubscribe={handleSubscribe}
              onBuySingleBook={handleBuySingleBook}
              isLoading={isCheckoutLoading}
              onOpenAuth={() => {
                setShowUpgradeModal(false);
                setAuthModalMode('register');
                setShowAuthModal(true);
              }}
            />

            {/* Auth Modal (for when guest decides to subscribe) */}
            <AuthModal
              isOpen={showAuthModal}
              onClose={() => setShowAuthModal(false)}
              onAuthSuccess={handleAuthSuccess}
              initialMode={authModalMode}
            />

            {!project ? (
              <ProjectSetup
                onProjectCreated={handleProjectCreated}
                onProjectLoaded={handleProjectLoaded}
                user={null}
                isGuestMode={true}
                onOpenAccount={() => setShowAuthModal(true)}
                onLogout={() => {
                  clearGuestSession();
                  setIsGuestMode(false);
                  setGuestSession(null);
                  setProject(null);
                }}
              />
            ) : (
              <WriterWorkspace
                project={project}
                updateSection={handleUpdateSection}
                updateSegment={handleUpdateSegment}
                user={null}
                isGuestMode={true}
                onUpgradeRequired={handleUpgradeRequired}
                onOpenAccount={() => setShowUpgradeModal(true)}
                onOpenAuth={() => {
                  setAuthModalMode('register');
                  setShowAuthModal(true);
                }}
                onBack={handleBackToSetup}
                onLogout={() => {
                  clearGuestSession();
                  setIsGuestMode(false);
                  setGuestSession(null);
                  setProject(null);
                }}
                onUserUpdate={() => { }}
                onUpdateBible={handleUpdateBible}
              />
            )}
          </div>
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Authenticated user flow
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <div className="min-h-screen bg-transparent">
          {/* Account Panel Modal */}
          <AccountPanel
            user={user!}
            isOpen={showAccountPanel}
            onClose={() => setShowAccountPanel(false)}
            onLogout={handleLogout}
            onPlanUpdated={handlePlanUpdated}
          />

          {!project ? (
            <ProjectSetup
              onProjectCreated={handleProjectCreated}
              onProjectLoaded={handleProjectLoaded}
              user={user!}
              onOpenAccount={() => setShowAccountPanel(true)}
              onLogout={handleLogout}
            />
          ) : (
            <WriterWorkspace
              project={project}
              updateSection={handleUpdateSection}
              updateSegment={handleUpdateSegment}
              user={user!}
              onOpenAccount={() => setShowAccountPanel(true)}
              onBack={handleBackToSetup}
              onLogout={handleLogout}
              onUserUpdate={setUser}
              onUpdateBible={handleUpdateBible}
            />
          )}
        </div>
      </Suspense>
    </ErrorBoundary>
  );
};

export default App;